import { api } from "../http";
import type { ReferenceRecord } from "../../../components/reference/types";
import { buildReferenceCacheKey, isNavigatorOffline, searchCachedReferences } from "../../offline/referenceCache";

export type EnvironmentalReferenceType =
  | "business-types"
  | "business-centres"
  | "food-locations"
  | "food-premises"
  | "vector-types"
  | "trap-types"
  | "vector-species"
  | "site-types"
  | "equipment-traps"
  | "water-sample-sources"
  | "water-quality-parameters";

function buildQuery(params: Record<string, unknown>) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
}

const prefixFor = (type: EnvironmentalReferenceType) => buildReferenceCacheKey("environmental", type);

function path(type: EnvironmentalReferenceType) {
  return `/api/environmental/references/${type}`;
}

export const environmentalReferencesApi = {
  async search(type: EnvironmentalReferenceType, params: { q?: string; limit?: number } = {}) {
    if (isNavigatorOffline()) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) return cached;
    }
    try {
      const query = buildQuery(params);
      return await api.get<ReferenceRecord[]>(`${path(type)}${query ? `?${query}` : ""}`);
    } catch (error) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) return cached;
      throw error;
    }
  },
  async create(type: EnvironmentalReferenceType, payload: Record<string, string | undefined>) {
    return api.post<ReferenceRecord, Record<string, string | undefined>>(path(type), payload);
  },
  async update(type: EnvironmentalReferenceType, id: string, payload: Record<string, string | undefined>) {
    return api.put<ReferenceRecord, Record<string, string | undefined>>(
      `${path(type)}/${encodeURIComponent(id)}`,
      payload,
    );
  },
};

export default environmentalReferencesApi;
